import { useState, useEffect } from 'react';
import { getRetentionAnalytics, CohortData } from '../../services/retentionService';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

const RetentionTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [retentionData, setRetentionData] = useState<any>(null);
  const [cohortType, setCohortType] = useState<'daily' | 'weekly' | 'monthly'>('monthly');

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getRetentionAnalytics(90, cohortType);
        setRetentionData(data);
      } catch (error) {
        console.error('Error loading retention data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [cohortType]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading retention analytics...</p>
      </div>
    );
  }

  if (!retentionData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No retention data available</p>
      </div>
    );
  }

  // Prepare retention curve data
  const retentionCurveData = {
    labels: retentionData.cohorts.map((c: CohortData) => c.cohort),
    datasets: [
      {
        label: 'D1 Retention',
        data: retentionData.cohorts.map((c: CohortData) => c.retentionRates.d1),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: false,
      },
      {
        label: 'D7 Retention',
        data: retentionData.cohorts.map((c: CohortData) => c.retentionRates.d7),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: false,
      },
      {
        label: 'D30 Retention',
        data: retentionData.cohorts.map((c: CohortData) => c.retentionRates.d30),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: false,
      },
      {
        label: 'D90 Retention',
        data: retentionData.cohorts.map((c: CohortData) => c.retentionRates.d90),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: false,
      },
    ],
  };

  // Cohort LTV data
  const cohortLTVData = {
    labels: retentionData.cohorts.slice(-12).map((c: CohortData) => c.cohort),
    datasets: [
      {
        label: 'Revenue per Cohort',
        data: retentionData.cohorts.slice(-12).map((c: CohortData) => c.revenue),
        backgroundColor: '#8b5cf6',
      },
    ],
  };

  // Churn rate by tier
  const churnKeys = Array.from(retentionData.retentionMetrics.churnRateByTier.keys());
  const churnValues = Array.from(retentionData.retentionMetrics.churnRateByTier.values());
  const churnByTierData = {
    labels: churnKeys as string[],
    datasets: [
      {
        label: 'Churn Rate (%)',
        data: churnValues as number[],
        backgroundColor: '#ef4444',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 'bold' }}>
          Retention & Cohort Analysis
        </h1>
        <select
          value={cohortType}
          onChange={(e) => setCohortType(e.target.value as 'daily' | 'weekly' | 'monthly')}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="daily">Daily Cohorts</option>
          <option value="weekly">Weekly Cohorts</option>
          <option value="monthly">Monthly Cohorts</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Return Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {retentionData.retentionMetrics.returnRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Days Between Sessions</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {retentionData.retentionMetrics.avgDaysBetweenSessions.toFixed(1)}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Cohorts</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {retentionData.cohorts.length}
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Retention Curves by Cohort</h3>
          <LineChart data={retentionCurveData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Cohort Lifetime Value</h3>
          <BarChart data={cohortLTVData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Churn Rate by Subscription Tier</h3>
          <BarChart data={churnByTierData} height={300} />
        </div>
      </div>

      {/* Cohort Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Cohort Details
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Cohort</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Users</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>D1</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>D7</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>D30</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>D90</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {retentionData.cohorts.slice(-20).reverse().map((cohort: CohortData, index: number) => (
              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{cohort.cohort}</td>
                <td style={{ padding: '1rem' }}>{cohort.totalUsers}</td>
                <td style={{ padding: '1rem' }}>{cohort.retentionRates.d1.toFixed(1)}%</td>
                <td style={{ padding: '1rem' }}>{cohort.retentionRates.d7.toFixed(1)}%</td>
                <td style={{ padding: '1rem' }}>{cohort.retentionRates.d30.toFixed(1)}%</td>
                <td style={{ padding: '1rem' }}>{cohort.retentionRates.d90.toFixed(1)}%</td>
                <td style={{ padding: '1rem' }}>${cohort.revenue.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RetentionTab;

